import { computed, ref, watch, type ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Language, TranslationMode } from '@lingvo-news/contracts';
import { parseCommonRouteParamsFromParams } from './useCommonRouteParams';

function getFirstStringParam(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    return value[0];
  }
  return undefined;
}

function extractModeParam(value: unknown): TranslationMode {
  let raw: string | undefined;

  if (typeof value === 'string') {
    raw = value;
  } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    raw = value[0];
  }

  if (!raw) {
    return TranslationMode.Partial;
  }

  const normalized = raw.toLowerCase();
  if (normalized === 'full') {
    return TranslationMode.Full;
  }
  if (normalized === 'partial') {
    return TranslationMode.Partial;
  }

  return TranslationMode.Partial;
}

export interface CurrentPreferences {
  nativeLanguage: Language | null;
  learnedLanguage: Language | null;
  mode: TranslationMode;
}

export interface UseCurrentPreferencesReturn {
  nativeLanguage: ComputedRef<Language | null>;
  learnedLanguage: ComputedRef<Language | null>;
  mode: ComputedRef<TranslationMode>;
  categoryId: ComputedRef<string | undefined>;
  /**
   * Normalizes the route if the language params are not in the correct format.
   * Returns true if the route was normalized.
   */
  normalizeRoute: (routeName: string, additionalParams?: Record<string, string>) => boolean;
}

/**
 * Composable to extract and manage current language and mode preferences from route params.
 * Provides reactive access to nativeLanguage, learnedLanguage, and mode.
 * 
 * Route params should be named: nativeLanguage, learnedLanguage, mode, categoryId
 */
export function useCurrentPreferences(): UseCurrentPreferencesReturn {
  const route = useRoute();
  const router = useRouter();

  function parseLanguage(value: unknown): Language | null {
    return parseCommonRouteParamsFromParams({ nativeLanguage: value, learnedLanguage: null, mode: null }).nativeLanguage;
  }

  const nativeLanguage = computed(() => {
    return parseLanguage(route.params.nativeLanguage);
  });

  const learnedLanguage = computed(() => {
    return parseLanguage(route.params.learnedLanguage);
  });

  const mode = computed(() => {
    return extractModeParam(route.params.mode);
  });

  const categoryId = computed(() => {
    const param = route.params.categoryId;
    if (typeof param === 'string' && param.trim()) {
      return param;
    }
    return undefined;
  });

  const normalizeRoute = (routeName: string, additionalParams?: Record<string, string>): boolean => {
    const rawNative = getFirstStringParam(route.params.nativeLanguage);
    const rawLearned = getFirstStringParam(route.params.learnedLanguage);
    const nativeLangValue = parseLanguage(route.params.nativeLanguage);
    const learnedLangValue = parseLanguage(route.params.learnedLanguage);
    const modeValue = extractModeParam(route.params.mode);

    const shouldNormalize =
      (!!nativeLangValue && !!rawNative && rawNative !== nativeLangValue) ||
      (!!learnedLangValue && !!rawLearned && rawLearned !== learnedLangValue);

    if (shouldNormalize) {
      if (!nativeLangValue || !learnedLangValue) {
        return false;
      }
      const params: Record<string, string> = {
        nativeLanguage: nativeLangValue,
        learnedLanguage: learnedLangValue,
        mode: modeValue.toLowerCase(),
        ...additionalParams
      };

      void router.replace({ name: routeName, params });
      return true;
    }

    return false;
  };

  return {
    nativeLanguage,
    learnedLanguage,
    mode,
    categoryId,
    normalizeRoute
  };
}












